import * as React from "react";
import { Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, ChevronDown } from "lucide-react";
import { Button } from "../ui/Button";
import { searchProducts, getVendors, getProductsByVendor } from "../../services/api";
import type { Product, Vendor, SearchProductsResponse } from "../../services/api";

interface ProductSidebarProps {
  selectedProduct: string;
  onProductSelect: (productId: string) => void;
}

const PAGE_SIZE = 24;

export function ProductSidebar({ selectedProduct, onProductSelect }: ProductSidebarProps) {
  const [products, setProducts] = React.useState<Product[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  // Vendor state
  const [vendors, setVendors] = React.useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = React.useState("");
  const [vendorProducts, setVendorProducts] = React.useState<Product[]>([]);
  const [selectedName, setSelectedName] = React.useState("");

  // Load vendors on mount
  React.useEffect(() => {
    getVendors()
      .then((res) => setVendors(res.vendors || []))
      .catch(() => {});
  }, []);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch products when page, search, or vendor changes
  React.useEffect(() => {
    if (selectedVendor) {
      fetchVendorProducts();
    } else {
      fetchProducts();
    }
  }, [page, debouncedSearch, selectedVendor]);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: PAGE_SIZE };
      if (debouncedSearch.trim()) {
        params.name = debouncedSearch.trim();
      }
      const response: SearchProductsResponse = await searchProducts(params);
      setProducts(response.data || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error("Failed to search products:", error);
      setProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVendorProducts = async () => {
    setIsLoading(true);
    try {
      const response = await getProductsByVendor(selectedVendor, 200);
      let items = response.products || [];
      // Client-side search filter for vendor products
      if (debouncedSearch.trim()) {
        const q = debouncedSearch.trim().toLowerCase();
        items = items.filter((p) => p.name.toLowerCase().includes(q));
      }
      setVendorProducts(items);
      setTotal(items.length);
      // Client-side pagination
      const start = (page - 1) * PAGE_SIZE;
      setProducts(items.slice(start, start + PAGE_SIZE));
    } catch (error) {
      console.error("Failed to fetch vendor products:", error);
      setProducts([]);
      setVendorProducts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVendorChange = (vendorId: string) => {
    setSelectedVendor(vendorId);
    setPage(1);
    setVendorProducts([]);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col h-full">
      {/* Vendor selector */}
      <div className="p-2 border-b">
        <div className="relative">
          <select
            value={selectedVendor}
            onChange={(e) => handleVendorChange(e.target.value)}
            className="w-full h-8 px-2 pr-7 text-xs rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring appearance-none cursor-pointer"
          >
            <option value="">All Products</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        </div>
      </div>

      {/* Search bar */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-8 pl-7 pr-7 text-xs rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Selected product + count */}
      <div className="px-2 py-1 text-xs border-b">
        {selectedProduct && selectedName ? (
          <div className="font-medium text-foreground truncate">{selectedName}</div>
        ) : null}
        <span className="text-muted-foreground">
          {total} product{total !== 1 ? "s" : ""}
          {debouncedSearch && ` matching "${debouncedSearch}"`}
        </span>
      </div>

      {/* Product grid */}
      <div className="flex-1 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="grid grid-cols-4 gap-1">
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="aspect-square rounded bg-muted animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-xs text-muted-foreground">
            No products found
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-1">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => { onProductSelect(product.id); setSelectedName(product.name); }}
                className={`aspect-square rounded overflow-hidden border-2 transition-all hover:scale-105 relative group ${
                  selectedProduct === product.id
                    ? "border-primary ring-1 ring-primary/30"
                    : "border-transparent hover:border-muted-foreground/30"
                }`}
                title={`${product.name}${product.sizeName ? ` (${product.sizeName})` : ""}`}
              >
                {product.thumbnailPath ? (
                  <img
                    src={product.thumbnailPath}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <ImageIcon className="h-3 w-3 text-muted-foreground" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 py-1.5 border-t text-xs">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
            className="h-6 px-1.5"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
            className="h-6 px-1.5"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
